/**
 * Admin card for rolling out gated What's New entries to all users.
 *
 * Visible only when the admin endpoint accepts the calling user (admin flag
 * or first-owner email). Lets the owner flip a beta-gated entry public,
 * which also resets stale `last_seen_feature_version` stamps so the modal
 * re-fires across the user base.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Sparkles, Loader2, Lock, Globe, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

import { formatDate } from '@/lib/dateUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WhatsNewAdminCard({ user }) {
  const [entries, setEntries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // version currently being toggled

  const load = async () => {
    if (!user?.is_admin) { setEntries(false); setLoading(false); return; }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/whatsnew/status`, {
        headers: { 'X-User-Id': user.id },
      });
      setEntries(r.data?.entries || []);
    } catch (e) {
      if (e?.response?.status === 403) {
        setEntries(false); // not an admin — render nothing
      } else {
        setEntries([]);
        toast.error('Could not load rollout status.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.id) load();
  }, [user?.id]);

  const toggle = async (version, makePublic) => {
    setBusy(version);
    try {
      const r = await axios.post(
        `${API}/admin/whatsnew/${encodeURIComponent(version)}/rollout`,
        null,
        { headers: { 'X-User-Id': user.id }, params: { public: makePublic } }
      );
      const reNotified = r.data?.users_re_notified ?? 0;
      toast.success(
        makePublic
          ? `Rolled out ${version} — ${reNotified} user(s) will see it on next login.`
          : `Reverted ${version} to gated.`
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rollout failed.');
    } finally {
      setBusy(null);
    }
  };

  if (entries === false) return null; // not admin
  if (loading && entries === null) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-center gap-2 text-sm text-slate-500" data-testid="whatsnew-admin-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading rollout status…
      </div>
    );
  }
  if (!entries || entries.length === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="whatsnew-admin-card">
      <div className="px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-purple-50 to-blue-50 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <Sparkles className="w-4 h-4 text-purple-600" />
          <div>
            <h3 className="text-sm font-bold text-slate-900">What's New — rollout control</h3>
            <p className="text-xs text-slate-500">Gated entries are only visible to your account. Flip public when you're done testing — every user will see it on their next login.</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={load}
          disabled={loading}
          data-testid="whatsnew-admin-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      <div className="divide-y divide-slate-100">
        {entries.map((e) => {
          const isPublic = e.currently_public;
          const wasDefaultPublic = e.is_public_default;
          return (
            <div key={e.version} className="px-5 py-3 flex items-center gap-3" data-testid={`whatsnew-admin-row-${e.version}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate">
                  {e.title}
                  <span className="ml-2 text-[10px] uppercase tracking-wider font-bold text-slate-400">{e.version}</span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5">
                  {isPublic ? (
                    <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 font-semibold">
                      <Globe className="w-3 h-3" /> Visible to all users
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 font-semibold">
                      <Lock className="w-3 h-3" /> Beta — you only
                    </span>
                  )}
                  <span className="text-slate-400">·</span>
                  <span>{formatDate(e.released)}</span>
                </div>
              </div>
              {!wasDefaultPublic && (
                <Button
                  size="sm"
                  variant={isPublic ? 'outline' : 'default'}
                  onClick={() => toggle(e.version, !isPublic)}
                  disabled={busy === e.version}
                  className={isPublic ? 'text-amber-700 border-amber-200 hover:bg-amber-50' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}
                  data-testid={`whatsnew-admin-toggle-${e.version}`}
                >
                  {busy === e.version
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : (isPublic ? 'Revert to gated' : 'Roll out to all users')}
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
